import { useState, useEffect } from "react";
import { getCachedBlob, CachedBlob } from "@/shared/lib/blob-cache-persistent";

export interface ResolvedFile {
  original: string;
  resolved: string | File;
}

interface UseFileLoadResult {
  items: ResolvedFile[];
  loading: boolean;
  error: Error | null;
}

export const useFileLoad = (
  fileSources: string[] | null | undefined
): UseFileLoadResult => {
  const [items, setItems] = useState<ResolvedFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!fileSources || fileSources.length === 0) {
      setItems([]);
      return;
    }

    let isCancelled = false;

    const loadFiles = async () => {
      setLoading(true);
      setError(null);

      const loadedItems: ResolvedFile[] = [];

      for (const original of fileSources) {
        try {
          let resolved: string | File;

          if (
            original.startsWith("blob:") ||
            original.startsWith("http://") ||
            original.startsWith("https://")
          ) {
            resolved = original;
          } else {
            const cached: CachedBlob = await getCachedBlob(original);
            resolved = cached.url;
          }

          loadedItems.push({ original, resolved });
        } catch (err) {
          console.warn("Failed to load file:", original, err);
          // Даже при ошибке сохраняем элемент (можно показать placeholder)
          loadedItems.push({ original, resolved: original });
        }
      }

      if (!isCancelled) {
        setItems(loadedItems);
      }
    };

    loadFiles();

    return () => {
      isCancelled = true;
    };
  }, [fileSources]);

  return { items, loading, error };
};
